import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Drawer,
  CircularProgress,
  Alert,
  Paper,
  TablePagination,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useCustomers } from '../../hooks/customer/useCustomers';
import { useCustomerActions } from '../../hooks/customer/useCustomerActions';
import CustomerTable from '../../components/customers/CustomerTable';
import CustomerForm from '../../components/customers/CustomerForm';
import CustomerDetail from './CustomerDetail';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { Customer, CreateCustomerDTO } from '../../types/customer';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';
import { Search } from '@mui/icons-material';

const CustomersView = () => {
  // ---------- pagination ----------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<'firstName' | 'phone' | 'city' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [searchTerm, setSearchTerm] = useState('');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { customers, total, isLoading, error, refetch } = useCustomers(
    page,
    pageSize,
    sortBy,
    sortOrder,
    debouncedSearch
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { createCustomer, updateCustomer, deleteCustomer } = useCustomerActions();

  // ---------- UI ----------
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, customerId: 0 });
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const showNotification = (m: string, s: SEVERITY) =>
    setNotification({ open: true, message: m, severity: s });

  const handleAdd = () => {
    setSelectedCustomer(null);
    setIsFormOpen(true);
  };
  const handleEdit = (c: Customer) => {
    setSelectedCustomer(c);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleDeleteClick = (id: number) => setConfirmDialog({ open: true, customerId: id });
  const handleDeleteConfirm = async () => {
    try {
      await deleteCustomer(confirmDialog.customerId);
      await refetch();
      showNotification('Customer deleted', SEVERITY.SUCCESS);
      setIsDetailOpen(false);
    } catch {
      showNotification('Delete failed', SEVERITY.ERROR);
    } finally {
      setConfirmDialog({ open: false, customerId: 0 });
    }
  };

  const handleSubmit = async (data: CreateCustomerDTO) => {
    try {
      if (selectedCustomer) {
        await updateCustomer(selectedCustomer.id, data);
        showNotification('Updated', SEVERITY.SUCCESS);
      } else {
        await createCustomer(data);
        showNotification('Created', SEVERITY.SUCCESS);
      }
      await refetch();
      setIsFormOpen(false);
      setSelectedCustomer(null);
    } catch {
      showNotification('Save failed', SEVERITY.ERROR);
    }
  };

  const handleSelect = (c: Customer) => {
    setSelectedCustomer(c);
    setIsDetailOpen(true);
  };
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column as any);
      setSortOrder('ASC');
    }
    setPage(1); // reset to first page on new sort
  };

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Customers
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {customers.length || searchTerm ? (
            <TextField
              placeholder="Search by name…"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          ) : null}
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={20} />}
            onClick={handleAdd}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !debouncedSearch && !customers.length && !searchTerm ? (
        <Paper elevation={0}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
          >
            <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              No customers found
            </Typography>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Plus size={20} />}
              onClick={handleAdd}
              sx={{ mt: 3 }}
            >
              Add Customer
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <CustomerTable
            customers={customers}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onSelect={handleSelect}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <TablePagination
            component="div"
            sx={{ display: 'flex', justifyContent: 'center' }}
            count={total}
            page={page - 1}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}

      <Drawer
        anchor="right"
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: '70%', xl: '50%' }, p: 0 } }}
      >
        <CustomerForm
          customer={selectedCustomer ?? undefined}
          onSubmit={handleSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: '70%', xl: '50%' }, p: 0 } }}
      >
        {selectedCustomer && (
          <CustomerDetail
            customer={selectedCustomer}
            onClose={() => setIsDetailOpen(false)}
            onEdit={() => {
              setIsDetailOpen(false);
              handleEdit(selectedCustomer);
            }}
            onDelete={() => handleDeleteClick(selectedCustomer.id)}
          />
        )}
      </Drawer>

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, customerId: 0 })}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default CustomersView;
